import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Link,
  Button,
  Tabs,
  Tab,
  Slider,
  Tooltip,
} from '@mui/material';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TableChartIcon from '@mui/icons-material/TableChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import potatoChipIcon from '../../Assets/svgs/potato-chips.svg';
import filterIcon from '../../Assets/svgs/Filter.svg';
import tortillaIcon from '../../Assets/svgs/tortilla.svg';
import extrudedIcon from '../../Assets/svgs/extruded.svg';
import saltySnacksIcon from '../../Assets/svgs/salty-snacks.svg';
import commonIcon from '../../Assets/svgs/common-section.svg';
import KeyTakeawaysIcon from '../../Assets/svgs/key-takeaways-icon.svg';
import OverallInsightsIcon from '../../Assets/svgs/insights.svg';
import RegionalInsightsIcon from '../../Assets/svgs/regional-insights-icon.svg';
import RetailerInsightsIcon from '../../Assets/svgs/retailer-insights-icon.svg';
import PepsicoInsightsIcon from '../../Assets/svgs/pepsico logo.svg';
import CompetitorInsightsIcon from '../../Assets/svgs/competitor insights.svg';
import PricePointInsightsIcon from '../../Assets/svgs/price-point-insights-icon.svg';
import PromoInsightsIcon from '../../Assets/svgs/promo.svg';
import MaerketInsightsIcon from '../../Assets/svgs/market icon.svg';
import eventEmitter from './EventEmitter';
import {getInsightSectionHelpText} from './FilterHelp';
import {callLogEvent} from '../../Common/Util';

/* ── Pure helpers hoisted outside components to avoid re-creation on every render ── */
const extractTextContent = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextContent).join('');
  }
  if (React.isValidElement(node)) {
    return extractTextContent(node.props.children);
  }
  return '';
};

const normalizeInsightSectionName = (value: string) =>
  (value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

const uniformSectionTone = {
  headerBg: '#f2f7ff',
  headerBorder: '#d4e4fb',
  text: '#264a73',
  detailBg: '#f8fbff',
  detailBorder: '#e0ebf9',
};

/* ── CollapsibleTableSection: accordion wrapper for table-containing sections ── */
const CollapsibleTableSection = ({
  header,
  children,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: -1, mb: 2.5 }}>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center' }}
      >
        <Box sx={{ flex: 1 }}>{header}</Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ color: '#264a73', fontSize: 22, ml: 0.5 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: '#264a73', fontSize: 22, ml: 0.5 }} />
        )}
      </Box>
      {open && (
        <Box sx={{ mt: 0.5, px: 1, py: 0.8 }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

interface RawTextData {
  chatMessage: string;
  images: Record<string, string[]>;
  chart_insights?: Record<string, Array<Record<string, string>>>;
  presentation_deck?:
    | {
      data: string;
      content_type: string;
      file_extension: string;
      filename: string;
    }
    | Array<{
    data: string;
    content_type: string;
    file_extension: string;
    filename: string;
    }>;
}

interface RenderSummaryProps {
  rawText: RawTextData;
  isPrinting?: boolean;
  allSectionsExpanded?: boolean;
  setallSectionsExpanded?: (value: boolean) => void;
  activeInsightSection?: string | null;
  hideSelectedInsightHeader?: boolean;
}

interface MarkdownRendererProps {
  content: string;
  rawText: RawTextData;
  activeInsightSection?: string | null;
  hideSelectedInsightHeader?: boolean;
  // openGallery optionally accepts an openIndex to open a specific image in the viewer
  openGallery: (
    sectionName: string,
    sectionImages: any,
    openIndex?: number
  ) => void;
  iconMap: Record<string, string>; // Changed to string for SVG paths
  commonIcon: string;
  allSectionsExpanded?: boolean;
}
const MarkdownRenderer = ({
  content,
  rawText,
  activeInsightSection,
  hideSelectedInsightHeader,
  openGallery,
  iconMap,
  commonIcon,
  allSectionsExpanded,
}: MarkdownRendererProps) => {
  const handleAskQuestionClick = useCallback((event: any, question: string) => {
    event.preventDefault();
    event.stopPropagation();
    callLogEvent('CLICKED_ON_NEW_DAILOG_NEW_CHAT_BUTTON', question);
    eventEmitter.emit('openAskDialogue', { message: question });
  }, []);

  const transformInteractiveQuestions = (source: string) => {
    const normalizedSource = source
      .replace(/\r\n/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p>/gi, '\n')
      .replace(/<\/?p>/gi, '')
      .replace(
        /(\*\*|#{1,6}\s*)?(Query-Ready Analytical Questions|Analytical Questions)(\*\*)?:?/gi,
        '\n$2\n'
      )
      .replace(/(^|\n)\s*(\d+\.\s+)/g, '$1$2');

    const lines = normalizedSource.split('\n');
    const transformedLines: string[] = [];

    const escapeHtml = (text: string) =>
      text
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      const trimmedLine = line
        .replace(/<[^>]+>/g, '')
        .replace(/\u00a0/g, ' ')
        .replace(/\*\*/g, '')
        .replace(/^#{1,6}\s*/, '')
        .trim();
      const headingMatch = trimmedLine.match(
        /^(Analytical Questions|Query-Ready Analytical Questions|Ask the Q\/A Assistant):?$/i
      );

      if (headingMatch) {
        const questions: string[] = [];
        let lookAheadIndex = index + 1;

        while (lookAheadIndex < lines.length) {
          const nextTrimmedLine = lines[lookAheadIndex]
            .replace(/<[^>]+>/g, '')
            .replace(/\u00a0/g, ' ')
            .replace(/\*\*/g, '')
            .replace(/^#{1,6}\s*/, '')
            .trim();
                    // Allow empty lines between heading and the first question item.
          if (!nextTrimmedLine) {
            if (questions.length === 0) {
              lookAheadIndex += 1;
              continue;
            }
            break;
          }

          const questionMatch = nextTrimmedLine.match(
            /^(?:\d+\.\s+|[-*]\s+)(.*)$/
          );
          if (!questionMatch) {
            break;
          }

          questions.push(questionMatch[1].trim());
          lookAheadIndex += 1;
        }

        if (questions.length > 0) {
          transformedLines.push(
            `<interactivequestions data-heading="${escapeHtml(
              headingMatch[1]
            )}">`
          );
          questions.forEach((question) => {
            transformedLines.push(
              `<questionitem>${escapeHtml(question)}</questionitem>`
            );
          });
          transformedLines.push(`</interactivequestions>`);
          index = lookAheadIndex - 1;
          continue;
        }
      }

      transformedLines.push(line);
    }

    return transformedLines.join('\n');
  };

  const components = useMemo(() => ({
    h1: ({ node, ...props }: any) => (
      <Typography
        // variant='h5'
         component='h1'
        gutterBottom
        {...props}
      />
    ),
    h2: ({ node, ...props }: any) => (
      <Typography
        // variant='h6'
        component='h2'
        gutterBottom
        {...props}
      />
    ),
    h3: ({ node, ...props }: any) => (
      <Typography
        variant='subtitle2'
        component='h3'
        sx={{ fontWeight: 'bold' }}
        gutterBottom
        {...props}
      />
    ),
    h4: ({ node, ...props }: any) => (
      <Typography
        variant='body2'
        sx={{ fontWeight: 'bold' }}
        {...props}
      />
    ),
    p: ({ node, ...props }: any) => (
      <Typography
        variant='body2'
        paragraph
        {...props}
      />
    ),
    a: ({ node, href, ...props }: any) => (
      <Link
        href={href}
        {...props}
      />
    ),
    ol: ({ node, ...props }: any) => (
      <ol
        style={{
          paddingLeft: '22px',
          marginTop: '4px',
          marginBottom: '1px',
        }}
      >
        {props.children}
      </ol>
    ),
    li: ({ node, ...props }: any) => (
      <li
      style={{
        marginLeft: '10px',
        marginBottom: '10px',
        fontSize: '14px',
        lineHeight: '1.6' }}>
        {props.children}
      </li>
    ),
    interactivequestions: ({ node, ...props }: any) => {
      const heading = props['data-heading'] || 'Query-Ready Analytical Questions';
      const questions = React.Children.toArray(props.children)
        .map((child) => extractTextContent(child).trim())
        .filter(Boolean);

      return (
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant='subtitle1'
            sx={{ fontWeight: 700, mb: 1.25 }}
          >
            {heading}
          </Typography>
          <Box
            sx={{ display: 'grid', justifyContent:"start", gap: 1 }}
          >
            {questions.map((question, index) => (
              <Box key={`${heading}-${index}`}>
                <Button
                  variant='text'
                  fullWidth
                  onClick={(event: any) => handleAskQuestionClick(event, question)}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    padding: '5px 14px',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: '1px solid #c9dbf4',
                    backgroundColor: '#eff5fd',
                    boxShadow: '0 1px 0 rgba(0, 0, 0, 0.02)',
                    color: '#4b5563',
                    fontSize: '11px !important',
                    fontWeight: 400,
                    lineHeight: 1.2,
                    textAlign: 'left',
                    '& .MuiButton-startIcon': {
                      marginRight: '10px',
                    },
                    '&:hover': {
                      backgroundColor: '#e7f0fb',
                      borderColor: '#b7cdeb',
                      boxShadow: '0 2px 6px rgba(24, 68, 125, 0.08)',
                    },
                  }}
                >
                  <Box
                    component='span'
                    sx={{
                      // minWidth: 'px',
                      fontWeight: 500,
                      fontSize: '11px !important',
                      // color: '#2f5f9f',
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}.
                  </Box>
                  <Box
                    component='span'
                    sx={{
                      flex: 1,
                      fontSize: '11px !important',
                      lineHeight: 1.35,
                    }}
                  >
                    {question}
                  </Box>
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      );
    },

    questionitem: ({ children }: any) => <>{children}</>,
    table: ({ node, ...props }: any) => (
      <TableContainer
        component={Paper}
        variant='outlined'
        sx={{ my: 1, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
      >
        <Table
          size='small'
          sx={{ minWidth: 650 }}
          {...props}
        />
      </TableContainer>
    ),
    thead: ({ node, ...props }: any) => <TableHead {...props} />,
    tbody: ({ node, ...props }: any) => <TableBody {...props} />,
    tr: ({ node, ...props }: any) => <TableRow {...props} />,
    th: ({ node, ...props }: any) => (
      <TableCell
        align='left'
        sx={{ fontWeight: 'bold', fontSize: '12px' }}
        {...props}
      />
    ),
    td: ({ node, ...props }: any) => (
      <TableCell
        sx={{ fontSize: '12px' }}
        {...props}
      />
    ),

    details: ({ node, ...props }: any) => {
      const childrenArray = React.Children.toArray(props.children);
      const summaryNode = childrenArray.find(
        (child): child is React.ReactElement =>
          React.isValidElement(child) &&
          (child.type === 'summary' || child.props?.node?.tagName === 'summary')
      );
      const otherChildren = childrenArray.filter(
        (child) => child !== summaryNode
      );
      const summaryText = summaryNode
        ? summaryNode.props.children
        : 'Show Filters';
      const summaryLabel = extractTextContent(summaryText).trim();
      const helpText = getInsightSectionHelpText(summaryLabel);

      const summaryString = extractTextContent(summaryText).toUpperCase();
      const matchedKey = Object.keys(iconMap).find((key) =>
        summaryString.includes(key)
      );
      const iconSrc = matchedKey ? iconMap[matchedKey] : commonIcon;
      const tone = uniformSectionTone;
      const sectionKey = normalizeInsightSectionName(summaryLabel);
      const primaryInsightSections = new Set([
        normalizeInsightSectionName('KEY TAKEAWAYS'),
        normalizeInsightSectionName('OVERALL INSIGHTS'),
        normalizeInsightSectionName('REGIONAL INSIGHTS'),
        normalizeInsightSectionName('RETAILER INSIGHTS'),
        normalizeInsightSectionName('PROMO INSIGHTS'),
        normalizeInsightSectionName('PEPSICO INSIGHTS'),
        normalizeInsightSectionName('COMPETITOR INSIGHTS'),
        normalizeInsightSectionName('PRICE POINT INSIGHTS'),
        normalizeInsightSectionName('SUMMARISED INSIGHTS'),
        normalizeInsightSectionName('MARKET INSIGHTS'),
      ]);
      const isPrimaryInsightSection =
        primaryInsightSections.has(sectionKey) ||
        Array.from(primaryInsightSections).some(
          (topLevel) => sectionKey.startsWith(topLevel) || topLevel.startsWith(sectionKey)
        );
      const normalizedActiveSection = normalizeInsightSectionName(activeInsightSection || 'PERFORMANCE TREE');
      const isInsightNavigationMode = Boolean(activeInsightSection);

      // Check only immediate children for a direct table element (no recursion)
      const containsTable = (nodes: any): boolean => {
        const arr = React.Children.toArray(nodes);
        return arr.some((n: any) => {
          if (React.isValidElement(n)) {
            // cast to any to avoid TS complaining about unknown props shape
            return (
              n.type === 'table' || (n as any).props?.node?.tagName === 'table'
            );
          }
          if (n && typeof n === 'object') {
            return (n as any).props?.node?.tagName === 'table';
          }
          return false;
        });
      };

      const hasTable = containsTable(otherChildren);

      const sectionHeader = (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%' ,
            px: 1,
            py: 0.75,
            borderRadius: '10px',
            border: `1px solid ${tone.headerBorder}`,
            background: tone.headerBg,
            boxShadow: '0 2px 8px rgba(15,23,42,0.05)',
          }}
        >
          {hasTable ? (
            <TableChartIcon
              sx={{ color: '#005CBC', marginRight: 1, fontSize: 20 }}
            />
          ) : (
            iconSrc && (
              <img
                src={iconSrc}
                alt='section icon'
                style={{ marginRight: '8px', height: '20px', width: '20px' }}
              />
            )
          )}
          <Typography
            variant='body2'
            sx={{fontWeight: 800, color: tone.text, letterSpacing: '0.1px', flex: 1}}
          >
            {summaryText}
          </Typography>
        </Box>
      );

      // In sidebar-driven insight mode, remove accordion behavior completely.
      if (isInsightNavigationMode) {
        if (isPrimaryInsightSection && sectionKey !== normalizedActiveSection) {
          return null;
        }

        const hideHeaderForSelectedPrimary =
          hideSelectedInsightHeader &&
          isPrimaryInsightSection &&
          sectionKey === normalizedActiveSection;

        if (hasTable) {
          return (
            <CollapsibleTableSection header={!hideHeaderForSelectedPrimary ? sectionHeader : null}>
              {otherChildren}
            </CollapsibleTableSection>
          );
        }

        return (
          <Box sx={{ mb: 1 }}>
            {!hideHeaderForSelectedPrimary ? sectionHeader : null}
            <Box
              sx={{
                mt: hideHeaderForSelectedPrimary ? 0 : 0.5,
                px: hideHeaderForSelectedPrimary ? 0 : 1,
                py: hideHeaderForSelectedPrimary ? 0 : 0.8,
              }}
            >
              {otherChildren}
            </Box>
          </Box>
        );
      }

      if (hasTable) {
        return (
          <CollapsibleTableSection header={sectionHeader}>
            {otherChildren}
          </CollapsibleTableSection>
        );
      }

      return (
        <Box sx={{ mb: 1 }}>
          {sectionHeader}
          <Box
            sx={{
              mt: 0.5,
              px: 1,
              py: 0.8,
            }}
          >
            {otherChildren}
          </Box>
        </Box>
      );
    },

    img: ({ src, alt }: any) => {
      const normalizeKey = (s: string) =>
        s.replace(/\s+/g, ' ').trim().toUpperCase();
      const searchKey = normalizeKey(alt || '');

      const imageKeys = Object.keys(rawText.images || {});
      const matchedKey = imageKeys.find(
        (key) => normalizeKey(key) === searchKey
      );

      const imgs = matchedKey ? rawText.images[matchedKey] : [];

      // Normalize imgs into items: Array<{name, src}>
      const items: Array<{ name: string; src: string }> = [];
      if (Array.isArray(imgs)) {
        imgs.forEach((elem: any, i: number) => {
          if (!elem) return;
          if (typeof elem === 'string') {
            items.push({ name: `Image ${i + 1}`, src: elem });
          } else if (typeof elem === 'object') {
            Object.entries(elem).forEach(([k, v]) => {
              if (v)
                items.push({
                  name: String(k).replace(/\.png$/i, ''),
                  src: String(v),
                });
            });
          }
        });
      } else if (imgs && typeof imgs === 'object') {
        Object.entries(imgs).forEach(([k, v]) => {
          if (v)
            items.push({
              name: String(k).replace(/\.png$/i, ''),
              src: String(v),
            });
        });
      }

      // console.log('🔎 <img> items:', items);
      if (!items.length) {
        return (
          <Typography
            variant='body2'
            sx={{ fontStyle: 'italic', color: 'gray' }}
          >
            No visuals available for "{alt}"
          </Typography>
        );
      }

      const chartGrid = (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            p: 2,
            backgroundColor: 'rgba(0,0,0,0.03)',
            borderRadius: '8px',
          }}
        >
          {items.length > 0 &&
            items.map((it, index) => (
              <Tooltip
                title={it.name}
                key={`thumb-${index}`}
                arrow
              >
                <Paper
                  elevation={2}
                  onClick={() => openGallery(alt, items, index)}
                  sx={{
                    cursor: 'pointer',
                    p: 0.5,
                    border: '1px solid #ddd',
                    transition: 'transform 0.12s, box-shadow 0.12s',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: 340,
                    boxSizing: 'border-box',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover .thumbOverlay': {
                      opacity: 1,
                      bgcolor: 'rgba(0,0,0,0.45)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 260,
                      display: 'flex',
                      alignItems: 'stretch',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: '#fff',
                    }}
                  >
                    <img
                      src={
                        it.src.startsWith('data:')
                          ? it.src
                          : `data:image/png;base64,${it.src}`
                      }
                      alt={`${alt} - ${it.name}`}
                      style={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: 'translateZ(0)',
                        imageRendering: 'auto',
                        display: 'block',
                      }}
                    />

                    <Box
                      className='thumbOverlay'
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.12s, background-color 0.12s',
                      }}
                    >
                      <Button
                        variant='contained'
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          openGallery(alt, items, index);
                        }}
                        sx={{
                          bgcolor: '#327ec9',
                          '&:hover': { bgcolor: '#659cd6' },
                        }}
                      >
                        Click to Expand
                      </Button>
                    </Box>
                  </Box>
                  <Typography
                    variant='caption'
                    sx={{
                      mt: 0.75,
                      textAlign: 'center',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {it.name}
                  </Typography>
                </Paper>
              </Tooltip>
            ))}
        </Box>
      );

      if (activeInsightSection) {
        return (
          <Box sx={{ mt: 0.5, mb: 1 }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: 700, color: '#005CBC', mb: 0.8 }}
            >
              Charts
            </Typography>
            {chartGrid}
          </Box>
        );
      }

      return (
        <Box sx={{ mt: 0.5, mb: 1 }}>
          <Typography
            variant='body2'
            sx={{ fontWeight: 700, color: '#005CBC', mb: 0.8 }}
          >
            Charts
          </Typography>
          {chartGrid}
        </Box>
      );
    },
  }), [
    rawText.images,
    openGallery,
    iconMap,
    commonIcon,
    allSectionsExpanded,
    handleAskQuestionClick,
    activeInsightSection,
    hideSelectedInsightHeader,
  ]);

  const renderMarkdownChunk = useCallback((chunk: string, key: string) => (
    <ReactMarkdown
      key={key}
      components={components}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
    >
      {chunk}
    </ReactMarkdown>
  ), [components]);

  return <>{renderMarkdownChunk(transformInteractiveQuestions(content), 'md-content')}</>;
};

// Memoize the MarkdownRenderer to avoid unnecessary remounts which reset internal state
const MemoizedMarkdownRenderer = React.memo(MarkdownRenderer);
const normalizeInlineMarkdownForHtml = (text: string) =>
  (text || '')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>');

const convertNumberedLinesToHtmlList = (body: string) => {
  const source = (body || '').replace(/\r\n/g, '\n');
  if (!source.trim()) return body;
  if (/<ol\b|<ul\b/i.test(source)) return body;

  const lines = source.split('\n');
  const items: string[] = [];
  let current = '';

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    const marker = line.match(/^\d+[\.)]\s+(.*)$/);
    if (marker) {
      if (current) items.push(current.trim());
      current = marker[1].trim();
      continue;
    }

    if (current) {
      current += ` ${line}`;
    }
  }

  if (current) items.push(current.trim());
  if (items.length < 2) return body;

  const htmlItems = items
    .map((item) => `<li>${normalizeInlineMarkdownForHtml(item)}</li>`)
    .join('\n');

  return `<ol>\n${htmlItems}\n</ol>`;
};

const normalizeDetailsBlocks = (text: string) =>
  (text || '').replace(
    /<details([^>]*)>\s*<summary([^>]*)>([\s\S]*?)<\/summary>([\s\S]*?)<\/details>/gi,
    (_match, detailsAttrs, summaryAttrs, summaryContent, detailsBody) => {
      const normalizedBody = convertNumberedLinesToHtmlList(detailsBody);
      return `<details${detailsAttrs}><summary${summaryAttrs}>${summaryContent}</summary>\n${normalizedBody}\n</details>`;
    }
  );

/* ── Pure function hoisted outside to avoid re-creation on every render ── */
const normalizeMarkdown = (text: string) => {
  let normalizedText = normalizeDetailsBlocks(text)
    .replace(/â€"/g, '-')
    .replace(/âˆ'/g, '-')
    .replace(/â€"/g, '-')
    .replace(/â€™/g, "'");

  // Normalize ordered-list markers so mixed formats like `1)` and `2.`
  // are parsed as one consistent list by markdown.
  normalizedText = normalizedText.replace(
    /(^|\n)(\s*)(\d+)[\.)](\s+)/g,
    '$1$2$3. '
  );

  normalizedText = normalizedText.replace(
    /([^\n])\n(###|-- \*\*|<details>)/g,
    '$1\n\n$2'
  );

  normalizedText = normalizedText.replace(
    /(<\/details>)\n(\*\*)/g,
    '$1\n\n$2'
  );

  normalizedText = normalizedText.replace(
    /([^\n])\n((?:19|20)\d{2}\.\s)/g,
    '$1 $2'
  );

  // Ensure chart insight text starts after a single blank line following Key Insight heading.
  normalizedText = normalizedText.replace(
    /(^|\n)(\s*(?:#{1,6}\s*)?(?:\*\*\s*)?Key Insights?(?:\s*\*\*)?\s*:?)(\n)(?!\n)/gi,
    '$1$2\n\n'
  );

  normalizedText = normalizedText.replace(/\n{3,}/g, '\n\n');

  return '\n' + normalizedText.trimStart();
};

const RenderSummary = ({ rawText, activeInsightSection, hideSelectedInsightHeader }: RenderSummaryProps) => {
  // previewGallery holds objects { name, src } so we can show filenames and base64 data
  const [previewGallery, setPreviewGallery] = useState<
    Array<{ name: string; src: string }>
  >([]);
  const [zoomLevels, setZoomLevels] = useState<Record<number, number>>({});
  // Default zoom used when no explicit per-image zoom is set
  const DEFAULT_ZOOM = 0.65;
  const [imageSizes, setImageSizes] = useState<
    Record<number, { width: number; height: number }>
  >({});
  const [galleryTitle, setGalleryTitle] = useState<string>('');
  const [galleryInsights, setGalleryInsights] = useState<Record<string, string>>({});
  // -1 = preview grid, 0..n = individual image tabs
  const [currentTab, setCurrentTab] = useState(-1);
  // whether the dialog should show its own preview grid (-1 mode)
  const [showDialogPreviewGrid, setShowDialogPreviewGrid] = useState(false);
  const allSectionsExpanded = false;

  // Ref for the scrollable viewport so we can implement wheel-to-pan
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Drag state as ref to avoid re-renders during drag
  const isDraggingRef = useRef(false);
  const dragStartX = useRef<number>(0);
  const dragStartScrollLeft = useRef<number>(0);

  const handleViewportWheel = (e: React.WheelEvent) => {
    const el = viewportRef.current;
    if (!el) return;
    // Translate vertical wheel into horizontal scroll for panning
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  };

  // Drag handlers for mouse (use refs to avoid re-renders during drag)
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    const el = viewportRef.current;
    if (!el) return;
    isDraggingRef.current = true;
    dragStartX.current = e.clientX;
    dragStartScrollLeft.current = el.scrollLeft;
    el.style.cursor = 'grabbing';
  }, []);

  const handleDragMove = useCallback((e: React.MouseEvent) => {
    const el = viewportRef.current;
    if (!el || !isDraggingRef.current) return;
    const dx = e.clientX - dragStartX.current;
    el.scrollLeft = dragStartScrollLeft.current - dx;
  }, []);

  const handleDragEnd = useCallback(() => {
    const el = viewportRef.current;
    if (el) el.style.cursor = 'default';
    isDraggingRef.current = false;
  }, []);

  // Touch support
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const el = viewportRef.current;
    if (!el) return;
    isDraggingRef.current = true;
    dragStartX.current = e.touches[0].clientX;
    dragStartScrollLeft.current = el.scrollLeft;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const el = viewportRef.current;
    if (!el || !isDraggingRef.current) return;
    const dx = e.touches[0].clientX - dragStartX.current;
    el.scrollLeft = dragStartScrollLeft.current - dx;
  }, []);

  const handleTouchEnd = useCallback(() => { isDraggingRef.current = false; }, []);

  // 2. Define the type for the function parameter
  // const handleToggleSection = (sectionTitle: string) => {
  //   setExpandedSections(prev => ({
  //     ...prev,
  //     [sectionTitle]: !prev[sectionTitle],
  //   }));
  // };
  const iconMap: Record<string, string> = React.useMemo(
    () => ({
      'POTATO CHIPS': potatoChipIcon,
      TORTILLA: tortillaIcon,
      EXTRUDED: extrudedIcon,
      'SALTY SNACKS': saltySnacksIcon,
      FILTER: filterIcon,
      'KEY TAKEAWAYS': KeyTakeawaysIcon,
      'OVERALL INSIGHTS': OverallInsightsIcon,
      'REGIONAL INSIGHTS': RegionalInsightsIcon,
      'RETAILER INSIGHTS': RetailerInsightsIcon,
      'PEPSICO INSIGHTS': PepsicoInsightsIcon,
      'COMPETITOR INSIGHTS': CompetitorInsightsIcon,
      'PRICE POINT INSIGHTS': PricePointInsightsIcon,
      'PROMO INSIGHTS': PromoInsightsIcon,
      'SUMMARISED INSIGHTS': commonIcon,
      'MARKET INSIGHTS': MaerketInsightsIcon,
    }),
    []
  );
  // const handleToggle = (header: string) => {
  //     setExpanded((prev) => ({ ...prev, [header]: !prev[header] }));
  // };

  const openGallery = React.useCallback(
    (sectionName: string, sectionImages: any, openIndex?: number) => {
      // sectionImages can be:
      // - an array of base64 strings
      // - an array of objects like [{ "Title": "base64" }, ...]
      // - an object mapping title->base64
      // - OR it may already be normalized to [{name,src}, ...]
      let items: Array<{ name: string; src: string }> = [];
      if (Array.isArray(sectionImages)) {
        // If already normalized (objects with name/src), use directly
        if (
          sectionImages.length > 0 &&
          typeof sectionImages[0] === 'object' &&
          sectionImages[0].hasOwnProperty('name') &&
          sectionImages[0].hasOwnProperty('src')
        ) {
          items = sectionImages
            .filter((it: any) => it && it.src)
            .map((it: any) => ({ name: String(it.name), src: String(it.src) }));
        } else {
          // Otherwise normalize array elements which may be strings or objects
          sectionImages.forEach((elem: any, i: number) => {
            if (!elem) return;
            if (typeof elem === 'string') {
              if (String(elem).trim() !== '')
                items.push({ name: `Image ${i + 1}`, src: String(elem) });
            } else if (typeof elem === 'object') {
              Object.entries(elem).forEach(([k, v]) => {
                if (v && String(v).trim() !== '')
                  items.push({
                    name: String(k).replace(/\.png$/i, ''),
                    src: String(v),
                  });
              });
            }
          });
        }
      } else if (sectionImages && typeof sectionImages === 'object') {
        Object.entries(sectionImages).forEach(([k, v]) => {
          if (v && String(v).trim() !== '')
            items.push({
              name: String(k).replace(/\.png$/i, ''),
              src: String(v),
            });
        });
      }

      if (items.length > 0) {
        setGalleryTitle(sectionName);
        setPreviewGallery(items);
        const normalizeKey = (s: string) => s.replace(/\s+/g, ' ').trim().toUpperCase();
        const insights: Record<string, string> = {};
        if (rawText.chart_insights) {
          const insightKey = Object.keys(rawText.chart_insights).find(
            (key) => normalizeKey(key) === normalizeKey(sectionName)
          );
          if (insightKey && Array.isArray(rawText.chart_insights[insightKey])) {
            rawText.chart_insights[insightKey].forEach((entry: Record<string, string>) => {
              Object.entries(entry).forEach(([chartName, insight]) => {
                insights[chartName] = insight;
              });
            });
          }
        }
        setGalleryInsights(insights);
        // If an index is provided, open that image directly; otherwise open first image
        const startIndex =
          typeof openIndex === 'number' &&
            openIndex >= 0 &&
            openIndex < items.length
            ? openIndex
            : 0;
        setCurrentTab(startIndex);
        setShowDialogPreviewGrid(false);
        setZoomLevels({});
      }
    },
    [rawText.chart_insights]
  );

  const handleDialogClose = useCallback(() => {
    setPreviewGallery([]);
    setCurrentTab(-1);
    setShowDialogPreviewGrid(false);
    setZoomLevels({});
    setGalleryInsights({});
  }, []);

  const handleZoomIn = (index: number) => {
    setZoomLevels((prev) => ({
      ...prev,
      [index]: (prev[index] || DEFAULT_ZOOM) + 0.1,
    }));
  };

  const handleZoomOut = (index: number) => {
    setZoomLevels((prev) => ({
      ...prev,
      [index]: Math.max(0.2, (prev[index] || DEFAULT_ZOOM) - 0.1), // Prevent zooming out too much
    }));
  };

  const resetZoom = (index: number) => {
    setZoomLevels((prev) => ({
      ...prev,
      [index]: DEFAULT_ZOOM,
    }));
  };
  // Navigate to previous image (if any)
  const handlePrev = () => {
    setCurrentTab((prev) => Math.max(0, (prev || 0) - 1));
  };

  // Navigate to next image (if any)
  const handleNext = () => {
    setCurrentTab((prev) => {
      const next = (prev || 0) + 1;
      return Math.min(previewGallery.length - 1, next);
    });
  };

  const handleSliderChange = (index: number, value: number | number[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setZoomLevels((prev) => ({ ...prev, [index]: v }));
  };

  const handleImageLoad =
    (index: number) => (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (!img) return;
      setImageSizes((prev) => ({
        ...prev,
        [index]: { width: img.naturalWidth, height: img.naturalHeight },
      }));
    };
  const handleDownload = (base64Data: string, filename: string) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Data}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const normalizeBase64Source = (value: string) =>
    value.startsWith('data:') ? value : `data:image/png;base64,${value}`;

  const getWrappedLines = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
  ) => {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(nextLine).width <= maxWidth) {
        currentLine = nextLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const handleDownloadWithInsight = async (
    imageSource: string,
    insightText: string | null,
    filename: string
  ) => {
    const imageUrl = normalizeBase64Source(imageSource || '');
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageUrl;
    });

    const hasInsight = !!insightText && insightText.trim().length > 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Unable to create canvas context');
    }

    const sidePadding = 30;
    const topPadding = 24;
    const sectionPadding = 18;
    const bulletIndent = 16;
    const insightLineHeight = 24;
    const headingHeight = 34;
    const headingToBodyGap = 16;

    const canvasWidth = Math.max(image.naturalWidth + sidePadding * 2, 1000);
    const imageDrawWidth = canvasWidth - sidePadding * 2;
    const imageDrawHeight = Math.round(
      (image.naturalHeight * imageDrawWidth) / image.naturalWidth
    );

    const headingText = 'Key Insight';
    const insightContentWidth = canvasWidth - sidePadding * 2 - sectionPadding * 2 - bulletIndent;
    const bulletLines: Array<{ text: string; startsBullet: boolean }> = [];

    if (hasInsight) {
      const bullets = insightText!
        .split(/(?<=[.!?])\s+/)
        .map((line) => line.trim())
        .filter(Boolean);

      ctx.font = '400 20px Arial, sans-serif';
      bullets.forEach((bullet) => {
        const wrapped = getWrappedLines(ctx, bullet, insightContentWidth);
        wrapped.forEach((line, index) => {
          bulletLines.push({ text: line, startsBullet: index === 0 });
        });
      });
    }

    const insightSectionHeight = hasInsight
      ? sectionPadding * 2 + headingHeight + headingToBodyGap + bulletLines.length * insightLineHeight
      : 0;

    canvas.width = canvasWidth;
    canvas.height = topPadding + (hasInsight ? insightSectionHeight + 20 : 0) + imageDrawHeight + 20;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (hasInsight) {
      const sectionY = topPadding;
      ctx.fillStyle = '#f2f7ff';
      ctx.fillRect(sidePadding, sectionY, canvasWidth - sidePadding * 2, insightSectionHeight);

      ctx.strokeStyle = '#d4e4fb';
      ctx.lineWidth = 1;
      ctx.strokeRect(sidePadding, sectionY, canvasWidth - sidePadding * 2, insightSectionHeight);

      ctx.fillStyle = '#264a73';
      ctx.font = '600 26px Arial, sans-serif';
      ctx.fillText(headingText, sidePadding + sectionPadding, sectionY + sectionPadding + 18);

      ctx.fillStyle = '#1f2f3f';
      ctx.font = '400 20px Arial, sans-serif';
      let lineY = sectionY + sectionPadding + headingHeight + headingToBodyGap;

      bulletLines.forEach((line) => {
        if (line.startsBullet) {
          ctx.fillText('•', sidePadding + sectionPadding, lineY);
        }
        ctx.fillText(line.text, sidePadding + sectionPadding + bulletIndent, lineY);
        lineY += insightLineHeight;
      });
    }

    const imageY = topPadding + (hasInsight ? insightSectionHeight + 20 : 0);
    ctx.drawImage(image, sidePadding, imageY, imageDrawWidth, imageDrawHeight);

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Memoize rendered content so it only re-computes when content-related deps change,
  // not on dialog/zoom/scroll state changes
  const renderedContent = useMemo(() => {
    const regex = /-- \*\*(.*?)\*\*\s*<hide>([\s\S]*?)<\/hide>/g;
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let text = normalizeMarkdown(rawText.chatMessage);

    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const [, header, hiddenContent] = match;
      const matchIndex = match.index;

      if (matchIndex > lastIndex) {
        const interText = text.slice(lastIndex, matchIndex);
        result.push(
          <MemoizedMarkdownRenderer
            key={`inter-${lastIndex}`}
            content={interText}
            rawText={rawText}
            activeInsightSection={activeInsightSection}
            hideSelectedInsightHeader={hideSelectedInsightHeader}
            openGallery={openGallery}
            iconMap={iconMap}
            commonIcon={commonIcon}
          />
        );
      }

      result.push(
        <MemoizedMarkdownRenderer
          key={`hide-${header}-${matchIndex}`}
          content={hiddenContent.trim()}
          rawText={rawText}
          activeInsightSection={activeInsightSection}
          hideSelectedInsightHeader={hideSelectedInsightHeader}
          openGallery={openGallery}
          iconMap={iconMap}
          commonIcon={commonIcon}
          allSectionsExpanded={allSectionsExpanded}
        />
      );

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      const remaining = text.slice(lastIndex);
      result.push(
        <MemoizedMarkdownRenderer
          key={`inter-${lastIndex}-end`}
          content={remaining}
          rawText={rawText}
          activeInsightSection={activeInsightSection}
          hideSelectedInsightHeader={hideSelectedInsightHeader}
          openGallery={openGallery}
          iconMap={iconMap}
          commonIcon={commonIcon}
          allSectionsExpanded={allSectionsExpanded}
        />
      );
    }

    return result;
  }, [rawText, allSectionsExpanded, activeInsightSection, openGallery, iconMap]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          width: '100%',
          borderBottom: 'none',
          marginBottom: '0px',
          paddingTop: '0px',
          paddingBottom: '0px',
        }}
      >
        {/* <Button
          onClick={() => setAllSectionsExpanded(!allSectionsExpanded)}
          size="small"
          sx={{
            textTransform: "none",
            alignSelf: "flex-start",
            padding: "4px 10px",
            minHeight: "28px",
            fontSize: "0.85rem",
            minWidth: "20px",
          }}
        >
          {allSectionsExpanded ? "Collapse All" : "Expand All"}
        </Button> */}
      </Box>
      {renderedContent}
      <Dialog
        open={previewGallery.length > 0}
        onClose={handleDialogClose}
        maxWidth='lg'
        fullWidth
        keepMounted
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              overflowX: 'auto',
            }}
          >
            {/* <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 'bold' }}>{galleryTitle}</Typography> */}
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto' }}>
              {previewGallery.map((it, i) => (
                <Button
                  key={`header-title-${i}`}
                  onClick={() => setCurrentTab(i)}
                  sx={{
                    textTransform: 'none',
                    // active tab uses brand blue (#005bbd) with white text; inactive uses muted text
                    color: i === currentTab ? '#ffffff' : 'text.secondary',
                    bgcolor: i === currentTab ? '#005bbd' : 'transparent',
                    minWidth: 'auto',
                    fontSize: 12,
                    px: 1,
                    borderRadius: 1,
                    boxShadow: i === currentTab ? 1 : 'none',
                    '&:hover': {
                      bgcolor: i === currentTab ? '#004a9a' : 'action.hover',
                    },
                  }}
                >
                  {it.name}
                </Button>
              ))}
            </Box>
          </Box>
          <IconButton
            aria-label='close'
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            position: 'relative',
            p: 0,
            height: '80vh',
            backgroundColor: '#2f2f2f',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(() => {
            const idx = currentTab >= 0 ? currentTab : 0;
            const item = previewGallery[idx] || previewGallery[0];
            const currentInsight = item ? galleryInsights[item.name] : null;
            if (!currentInsight) return null;
            const bullets = currentInsight.split(/(?<=[.!?])\s+/).filter((part) => part.trim());
            return (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  bgcolor: '#1a2a3a',
                  borderBottom: '1px solid #3a5068',
                  flexShrink: 0,
                  order: 0,
                }}
              >
                <Typography variant='caption' sx={{ fontWeight: 700, color: '#7db8f0', mb: 0.5, display: 'block', fontSize: 11 }}>
                  Key Insight
                </Typography>
                <Box component='ul' sx={{ m: 0, pl: 2, py: 0 }}>
                  {bullets.map((bullet, bulletIndex) => (
                    <Typography
                      component='li'
                      key={bulletIndex}
                      variant='body2'
                      sx={{ fontSize: 12, color: '#e0e8f0', lineHeight: 1.6, mb: 0.2 }}
                    >
                      {bullet}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })()}
          {/* Main viewer container */}
          {previewGallery && previewGallery.length > 0 && (
            <Box
              sx={{
                position: 'relative',
                flex: 1,
                order: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Left navigation */}
              {previewGallery.length > 1 && (
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,92,187,0.9)',
                    color: 'white',
                    width: 44,
                    height: 44,
                    opacity: 0.6,
                    '&:hover': { bgcolor: 'rgba(0,92,187,1)' },
                    '& .MuiSvgIcon-root': { fontSize: 28 },
                  }}
                  aria-label='previous image'
                >
                  <ChevronLeftIcon sx={{ color: 'white' }} />
                </IconButton>
              )}

              {/* (moved) header title strip is rendered in DialogTitle for compact header placement */}

              {/* Image viewport that allows panning when zoomed */}
              <Box
                ref={viewportRef}
                onWheel={handleViewportWheel}
                sx={{
                  overflow: 'auto',
                  maxHeight: '100%',
                  width: '100%',
                  display: 'block',
                }}
              >
                {(() => {
                  const idx = currentTab >= 0 ? currentTab : 0;
                  const item = previewGallery[idx] || previewGallery[0];
                  const scale = zoomLevels[idx] || DEFAULT_ZOOM;
                  const natural = imageSizes[idx];
                  const displayWidth = natural?.width
                    ? Math.round(natural.width * scale)
                    : undefined;

                  return (
                    <Box
                      sx={{
                        padding: 2,
                        width: displayWidth ? `${displayWidth}px` : 'auto',
                        minWidth: displayWidth ? `${displayWidth}px` : 'auto',
                      }}
                    >
                      <img
                        onLoad={handleImageLoad(idx)}
                        src={
                          item.src.startsWith('data:')
                            ? item.src
                            : `data:image/png;base64,${item.src}`
                        }
                        alt={item.name}
                        style={{
                          display: 'block',
                          width: displayWidth ? '100%' : 'auto',
                          maxWidth: 'none',
                          height: 'auto',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  );
                })()}
              </Box>

              {/* Right navigation */}
              {previewGallery.length > 1 && (
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,92,187,0.9)',
                    color: 'white',
                    width: 44,
                    height: 44,
                    opacity: 0.6,
                    '&:hover': { bgcolor: 'rgba(0,92,187,1)' },
                    '& .MuiSvgIcon-root': { fontSize: 28 },
                  }}
                  aria-label='next image'
                >
                  <ChevronRightIcon sx={{ color: 'white' }} />
                </IconButton>
              )}

              {/* Title / filename at top center removed per UX request */}

              {/* Bottom overlay controls */}
              <Paper
                elevation={6}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(0,0,0,0.72)',
                  color: 'white',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: 2,
                  minWidth: 360,
                }}
              >
                <IconButton
                  onClick={() =>
                    handleZoomOut(currentTab >= 0 ? currentTab : 0)
                  }
                  sx={{ color: 'white' }}
                  aria-label='zoom out'
                >
                  <ZoomOutIcon />
                </IconButton>

                <Slider
                  value={zoomLevels[currentTab] || DEFAULT_ZOOM}
                  min={0.2}
                  max={3}
                  step={0.01}
                  onChange={(e, v) =>
                    handleSliderChange(currentTab >= 0 ? currentTab : 0, v)
                  }
                  sx={{ color: 'white', mx: 1, flex: 1 }}
                  aria-label='zoom slider'
                />

                <IconButton
                  onClick={() => handleZoomIn(currentTab >= 0 ? currentTab : 0)}
                  sx={{ color: 'white' }}
                  aria-label='zoom in'
                >
                  <ZoomInIcon />
                </IconButton>

                <IconButton
                  onClick={() => resetZoom(currentTab >= 0 ? currentTab : 0)}
                  sx={{ color: 'white' }}
                  aria-label='reset zoom'
                >
                  <RestartAltIcon />
                </IconButton>

                <IconButton
                  onClick={() => {
                    const idx = currentTab >= 0 ? currentTab : 0;
                    const it = previewGallery[idx] || previewGallery[0];
                    const currentInsight = it ? galleryInsights[it.name] : null;
                    handleDownloadWithInsight(
                      it?.src || '',
                      currentInsight,
                      `${it?.name || 'download'}.png`
                    ).catch(() => {
                      const raw =
                        it?.src?.startsWith && it.src.startsWith('data:')
                          ? it.src.replace(/^data:image\/png;base64,/, '')
                          : it?.src;
                      handleDownload(raw, `${it?.name || 'download'}.png`);
                    });
                  }}
                  sx={{ color: 'white' }}
                  aria-label='download'
                >
                  <DownloadIcon />
                </IconButton>
              </Paper>
              {/* footer scrollbar removed per user request */}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RenderSummary;
